import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Avatar from 'flarum/common/components/Avatar';
import Button from 'flarum/common/components/Button';
import JoinBattleModal from './JoinBattleModal';
import CancelOpenBattleConfirmModal from './CancelOpenBattleConfirmModal';
/**
 * System post for an "open" battle lobby (see ArenaBattle::STATUS_WAITING /
 * CreateBattleController::handleOpenBattle). Renders a "waiting for
 * opponent" state with a Join button for any eligible viewer, or — once
 * this same post's content has been mutated by JoinBattleController on the
 * server (see ArenaBattleOpenPost::markJoined()) — the same "vs" matchup
 * view ArenaBattleStartPost shows for a normal challenge.
 */
export default class ArenaBattleOpenPost extends Component {
  challengerUser = null;
  opponentUser = null;
  oninit(vnode) {
    super.oninit(vnode);
    this.resolveUsers();
  }
  async resolveUsers() {
    const content = this.attrs.post.content();
    if (!content) return;
    const fromStore = id => app.store.getById('users', String(id)) ?? null;
    this.challengerUser = fromStore(content.challengerId);
    this.opponentUser = content.opponentId != null ? fromStore(content.opponentId) : null;
    const loads = [];
    if (!this.challengerUser) loads.push(app.store.find('users', content.challengerId).catch(() => null));
    if (!this.opponentUser && content.opponentId != null) loads.push(app.store.find('users', content.opponentId).catch(() => null));
    if (loads.length) {
      await Promise.all(loads);
      this.challengerUser = fromStore(content.challengerId);
      this.opponentUser = content.opponentId != null ? fromStore(content.opponentId) : null;
      m.redraw();
    }
  }

  /**
   * Avatar+name+win/loss/draw row — matches ArenaBattleStartPost's own
   * playerColumn() exactly. Used both for the "waiting" view's lone
   * challenger column (shown above the card, see view() below) and for the
   * "joined" view's side-by-side matchup, since once a lobby is joined it's
   * meant to look identical to a normal challenge's start post.
   */
  playerColumnWithStats(user, fallbackName) {
    const name = user?.displayName() || fallbackName;
    const stats = user?.attribute('arenaStats') ?? null;
    const profileUrl = user ? app.route('user', {
      username: user.username()
    }) : null;
    const nameEl = profileUrl ? m("a", {
      className: "ArenaBattleStartPost-playerName",
      href: profileUrl,
      onclick: e => {
        e.preventDefault();
        m.route.set(profileUrl);
      }
    }, name) : m("div", {
      className: "ArenaBattleStartPost-playerName"
    }, name);
    return m("div", {
      className: "ArenaBattleStartPost-player"
    }, m("div", {
      className: "ArenaBattleStartPost-avatar"
    }, user ? m(Avatar, {
      user: user
    }) : m("span", {
      className: "Avatar ArenaBattleStartPost-avatarFallback"
    }, (name[0] ?? '?').toUpperCase())), nameEl, stats && m("div", {
      className: "ArenaBattleStartPost-playerStats"
    }, m("span", {
      className: "ArenaBattleStartPost-stat ArenaBattleStartPost-stat--wins",
      title: app.translator.trans('forumaker-arena.lib.stats.wins')
    }, m("i", {
      className: "fas fa-trophy"
    }), " ", stats.wins ?? 0), m("span", {
      className: "ArenaBattleStartPost-stat ArenaBattleStartPost-stat--losses",
      title: app.translator.trans('forumaker-arena.lib.stats.losses')
    }, m("i", {
      className: "fas fa-skull"
    }), " ", stats.losses ?? 0), m("span", {
      className: "ArenaBattleStartPost-stat ArenaBattleStartPost-stat--draws",
      title: app.translator.trans('forumaker-arena.lib.stats.draws')
    }, m("i", {
      className: "fas fa-handshake"
    }), " ", stats.draws ?? 0)));
  }

  /**
   * "Waiting" view only — a centered pill tinted to match -info's hourglass
   * accent (see forum.less). Same non-default-only rule as
   * ArenaBattleStartPost.tsx's plain modifier line: only worth calling out
   * when it changes the rules from the default. Once the lobby is joined
   * (see the "joined" branch in view() below) the modifier is instead
   * printed as a plain third line inside ArenaStartPost-body, matching
   * ArenaBattleStartPost.tsx exactly — no badge there.
   */
  modifierBadge(content) {
    if (!content.modifier || content.modifier === 'classic') return null;
    return m("div", {
      className: "ArenaBattleOpenPost-modifierBadgeWrap"
    }, m("div", {
      className: "ArenaBattleOpenPost-modifierBadge"
    }, m("i", {
      className: "fas fa-dice"
    }), ' ', app.translator.trans('forumaker-arena.forum.battle_start.modifier_active', {
      modifier: app.translator.trans(`forumaker-arena.lib.modifiers.${content.modifier}`)
    })));
  }
  canJoin(content) {
    const actor = app.session.user;
    if (!actor || content.status !== 'waiting') return false;
    if (parseInt(actor.id()) === content.challengerId) return false;
    return true;
  }

  /** Guests can't join a battle, but the lobby is still open to them — show
   * a "log in to accept" button instead of hiding the action entirely. */
  canJoinAsGuest(content) {
    return !app.session.user && content.status === 'waiting';
  }

  /** True for the lobby's own creator, who can cancel it instead of joining. */
  isOwner(content) {
    const actor = app.session.user;
    if (!actor || content.status !== 'waiting') return false;
    return parseInt(actor.id()) === content.challengerId;
  }
  view() {
    const content = this.attrs.post.content();
    if (!content) return null;
    if (content.status === 'joined') {
      // Deliberately just "ArenaBattleStartPost EventPost" — no
      // ArenaBattleOpenPost class here, which carries its own bordered/
      // tinted-background card and would produce a double box stacked
      // underneath it. Once a lobby is joined it's meant to look exactly
      // like a normal challenge's start post.
      const showModifierLine = !!content.modifier && content.modifier !== 'classic';
      return m("div", {
        className: "ArenaBattleStartPost EventPost"
      }, m("div", {
        className: "ArenaBattleStartPost-wrap"
      }, m("div", {
        className: "ArenaBattleStartPost-versus"
      }, this.playerColumnWithStats(this.challengerUser, content.challengerName), m("span", {
        className: "ArenaBattleStartPost-vs"
      }, "VS"), this.playerColumnWithStats(this.opponentUser, content.opponentName ?? ''))), m("div", {
        className: "ArenaStartPost"
      }, m("div", {
        className: "ArenaStartPost-icon"
      }, m("i", {
        className: "fas fa-dungeon"
      })), m("div", {
        className: "ArenaStartPost-body"
      }, m("div", {
        className: "ArenaStartPost-title"
      }, app.translator.trans('forumaker-arena.forum.battle_start.first_turn', {
        name: m("strong", null, content.challengerName)
      })), m("div", {
        className: "ArenaStartPost-subtitle"
      }, app.translator.trans('forumaker-arena.forum.battle_start.first_turn_subtitle')), showModifierLine && m("div", {
        className: "ArenaStartPost-subtitle"
      }, app.translator.trans('forumaker-arena.forum.battle_start.modifier_active', {
        modifier: app.translator.trans(`forumaker-arena.lib.modifiers.${content.modifier}`)
      })))));
    }
    return m("div", {
      className: "ArenaBattleOpenPost EventPost"
    }, m("div", {
      className: "ArenaBattleOpenPost-challenger"
    }, this.playerColumnWithStats(this.challengerUser, content.challengerName)), this.modifierBadge(content), m("div", {
      className: "ArenaBattleOpenPost-wrap"
    }, m("div", {
      className: "ArenaBattleOpenPost-info"
    }, m("div", {
      className: "ArenaBattleOpenPost-icon"
    }, m("i", {
      className: "fas fa-hourglass-half"
    })), m("div", {
      className: "ArenaBattleOpenPost-body"
    }, m("div", {
      className: "ArenaBattleOpenPost-title"
    }, app.translator.trans('forumaker-arena.forum.battle_open.title', {
      name: m("strong", null, content.challengerName)
    }))), m("div", {
      className: "ArenaBattleOpenPost-actions"
    }, this.canJoin(content) && m(Button, {
      className: "Button Button--primary ArenaBattleOpenPost-joinBtn",
      onclick: () => app.modal.show(JoinBattleModal, {
        battleId: content.battleId,
        challengerName: content.challengerName,
        modifier: content.modifier
      })
    }, m("i", {
      className: "fas fa-user-plus"
    }), " ", app.translator.trans('forumaker-arena.forum.battle_open.join_btn')), this.canJoinAsGuest(content) && m(Button, {
      className: "Button Button--primary ArenaBattleOpenPost-joinBtn",
      onclick: () => app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal'))
    }, m("i", {
      className: "fas fa-user-plus"
    }), " ", app.translator.trans('forumaker-arena.forum.battle_open.join_guest_btn')), this.isOwner(content) && m(Button, {
      className: "Button ArenaBattleOpenPost-cancelBtn",
      onclick: () => app.modal.show(CancelOpenBattleConfirmModal, {
        battleId: content.battleId,
        onCancelled: () => m.route.set(app.route('index'))
      })
    }, m("i", {
      className: "fas fa-door-closed"
    }), " ", app.translator.trans('forumaker-arena.forum.battle_open.cancel_btn'))))));
  }
}
flarum.reg.add('forumaker-arena', 'forum/components/ArenaBattleOpenPost', ArenaBattleOpenPost);